import { Layer, ThemeContext } from 'grommet';

import { Button } from '@/components/Buttons';
import useSessionStorage from '@/hooks/useSessionStorage';

import TzPlusHero from './TzPlusHero';

/** Session-scoped, and deliberately separate from the guided journey's key: two modals, two dismissals. */
export const TZ_PLUS_MOBILE_DISMISSED_KEY = 'tzplus:mobileWelcomeDismissed';

/**
 * The TZ+ modal on a phone. A *welcome*, not the guided journey's mobile warning: it opens with the
 * zoo and points the visitor at what they can do here, and the desktop tour is its third line rather
 * than its subject.
 *
 * Only rendered by TourMobileGate, which has already established this is a mobile viewport and that
 * the tour has not been cleared. Reappears each session until then — a phone-only user can never
 * complete the tour, so they will keep seeing it, exactly as the All-Access warning behaves today.
 */
const TzPlusMobileWelcome = () => {
  const [dismissed, setDismissed] = useSessionStorage(TZ_PLUS_MOBILE_DISMISSED_KEY, false);

  if (dismissed) return null;

  const handleDismiss = () => setDismissed(true);

  return (
    <ThemeContext.Extend value={{ layer: { zIndex: '1000', container: { zIndex: '1001' } } }}>
      <Layer
        className="w-[80vw] max-w-[336px] !rounded-2xl"
        // Figma shows no close control, but a modal with no way out is a trap: esc and the backdrop
        // do what "Start Exploring" does.
        onEsc={handleDismiss}
        onClickOutside={handleDismiss}
        aria-labelledby="tzplus-mobile-title"
      >
        <div className="preflight overflow-hidden rounded-2xl bg-white">
          <TzPlusHero compact />

          <div className="preflight px-4 pb-4 pt-4">
            <h3
              id="tzplus-mobile-title"
              className="preflight preflight-h3 text-center text-[20px] font-bold leading-[1.25] text-primary-green"
            >
              Discover all of Toronto Zoo&rsquo;s LIVE stream habitats
            </h3>

            <p className="preflight preflight-p mt-3 text-center text-sm leading-5 text-grey-1">
              Search for Toronto Zoo, search for the animals, or find them in the home page in a moment the community loved.
            </p>

            <p className="preflight preflight-p mt-4 text-center text-sm font-bold leading-5 text-gray-500">
              For a more guided experience, enjoy a complimentary guided tour on desktop!
            </p>

            <Button variant="default" onClick={handleDismiss} className="mt-5 h-9 w-full font-bold">
              Start Exploring
            </Button>

            <p className="preflight preflight-p mt-3 text-center text-xs font-semibold text-gray-500">powered by zoolife</p>
          </div>
        </div>
      </Layer>
    </ThemeContext.Extend>
  );
};

export default TzPlusMobileWelcome;
